import { useEffect, useState, useCallback } from "react";
import { useAdmin } from "../contexts/AdminContext";
import { ErrorBanner } from "../components/ErrorBanner";
import type { MpesaPaymentEvent } from "../lib/types";

const PAGE_SIZE = 50n;

export function MpesaPaymentsPage() {
  const { actor } = useAdmin();
  const [items, setItems] = useState<MpesaPaymentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<MpesaPaymentEvent | null | "not-found">(null);
  const [searching, setSearching] = useState(false);

  const fetchPage = useCallback(async (cursor: string | null) => {
    if (!actor) return;
    setLoading(true);
    setError("");
    try {
      const res: { ok?: { items: MpesaPaymentEvent[]; nextCursor: [] | [string] }; err?: unknown } =
        await actor.listMpesaPayments(cursor ? [cursor] : [], PAGE_SIZE);
      if ("ok" in res) {
        setItems(res.ok!.items);
        setNextCursor(res.ok!.nextCursor[0] ?? null);
      } else {
        setError("Failed to load payments");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  function nextPage() {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, nextCursor]);
    fetchPage(nextCursor);
  }

  function prevPage() {
    if (cursorStack.length <= 1) return;
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    fetchPage(newStack[newStack.length - 1]);
  }

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q || !actor) return;
    setSearching(true);
    setError("");
    try {
      const res: { ok?: MpesaPaymentEvent; err?: unknown } = await actor.getMpesaPayment(q);
      if ("ok" in res) {
        setSearchResult(res.ok!);
      } else {
        setSearchResult("not-found");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Search failed");
      setSearchResult(null);
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setSearchQuery("");
    setSearchResult(null);
  }

  const hasPrev = cursorStack.length > 1;
  const showList = searchResult === null;

  return (
    <div>
      <h1 style={{ fontSize: 24, marginBottom: 24 }}>M-Pesa Payments</h1>

      <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Search by Transaction ID…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            maxWidth: 360,
            padding: "8px 12px",
            borderRadius: 4,
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
            fontSize: 13,
            fontFamily: "'DM Mono', monospace",
          }}
        />
        <button
          type="submit"
          disabled={searching || !searchQuery.trim()}
          style={actionBtnStyle(searching || !searchQuery.trim())}
        >
          {searching ? "…" : "Search"}
        </button>
        {searchResult !== null && (
          <button type="button" onClick={clearSearch} style={actionBtnStyle(false)}>
            Clear
          </button>
        )}
      </form>

      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

      {searchResult === "not-found" ? (
        <p style={{ color: "var(--color-muted)", fontSize: 14 }}>No payment found with that Transaction ID.</p>
      ) : searchResult !== null ? (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Tx ID</th>
                <th>Time (EAT)</th>
                <th>Amount (KES)</th>
                <th>Payer</th>
                <th>Account</th>
              </tr>
            </thead>
            <tbody>
              <PaymentRow ev={searchResult} />
            </tbody>
          </table>
        </div>
      ) : showList && loading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : showList && items.length === 0 ? (
        <p style={{ color: "var(--color-muted)" }}>No payments yet.</p>
      ) : showList ? (
        <>
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Tx ID</th>
                  <th>Time (EAT)</th>
                  <th>Amount (KES)</th>
                  <th>Payer</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ev) => <PaymentRow key={ev.transactionId} ev={ev} />)}
              </tbody>
            </table>
          </div>

          {(hasPrev || nextCursor) && (
            <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
              <button onClick={prevPage} disabled={!hasPrev} style={pageBtnStyle(!hasPrev)}>
                ← Prev
              </button>
              <button onClick={nextPage} disabled={!nextCursor} style={pageBtnStyle(!nextCursor)}>
                Next →
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function PaymentRow({ ev }: { ev: MpesaPaymentEvent }) {
  return (
    <tr>
      <td className="mono" style={{ fontSize: 12, color: "var(--color-text)", fontWeight: 500 }}>
        {ev.transactionId}
      </td>
      <td style={{ color: "var(--color-muted)", fontSize: 13, whiteSpace: "nowrap" }}>
        {ev.transactionTime}
      </td>
      <td className="mono" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        {String(ev.amount)}
      </td>
      <td style={{ fontSize: 13 }}>{ev.payer}</td>
      <td className="mono" style={{ fontSize: 12, color: "var(--color-muted)" }}>
        {ev.accountNumber[0] ?? "—"}
      </td>
    </tr>
  );
}

function actionBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "8px 16px",
    borderRadius: 4,
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: disabled ? "var(--color-muted)" : "var(--color-text)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 13,
    opacity: disabled ? 0.5 : 1,
  };
}

function pageBtnStyle(disabled: boolean | null): React.CSSProperties {
  return {
    padding: "8px 16px",
    borderRadius: 4,
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: disabled ? "var(--color-muted)" : "var(--color-text)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 13,
    opacity: disabled ? 0.5 : 1,
  };
}
